import { type EditAction, movableRootsOf, selectionBounds, transformActions } from '@/application';
import {
  angleMarkValuePosition,
  asPoint,
  hostCurve,
  type Evaluated,
  type EntitySource,
  isDraggable,
  parameterAt,
  transformSource,
  translation,
} from '@/domain/construction';
import { type Vec2, vec } from '@/domain/math';
import { type Entity, type EntityId, kindOf } from '@/domain/model';
import { entitiesWithin, hitTest, valueOf } from '@/engine';
import { type TransformBox } from '@/rendering';

import { clampParameter, snap } from '../Snapping';

import { inPlaneAt } from './pointFactory';
import {
  exceedsDragThreshold,
  type PointerInput,
  type Tool,
  type ToolContext,
  type ToolResponse,
} from './Tool';
import { boxOf, frameOf, geometryFor, isUsable } from './transformBox';

/**
 * Selection and dragging.
 *
 * Four explicit states. The alternative — an `isDragging` flag and a `dragStart` that may
 * or may not be set — is how a click that moves two pixels ends up either selecting
 * nothing or dragging something the user never grabbed.
 *
 * ```
 *   idle ──pointerDown on entity──► pressing ──moved past threshold──► dragging
 *     │                                 │                                 │
 *     │                            pointerUp                         pointerUp
 *     │                          (it was a click)                        │
 *     ▼                                 ▼                                ▼
 *   idle ◄──pointerUp─── marquee ◄──pointerDown on empty space──────── idle
 * ```
 */
type State =
  | { readonly name: 'idle' }
  | {
      readonly name: 'pressing';
      readonly target: EntityId;
      readonly origin: Vec2;
      readonly additive: boolean;
    }
  | {
      readonly name: 'dragging';
      readonly target: EntityId;
      readonly origin: Vec2;
      /** Offset from the pointer to the entity's position when the drag began. */
      readonly grabOffset: Vec2;
      /**
       * The source as it was when the gesture began.
       *
       * Everything except a point is moved by re-deriving from this rather than by nudging
       * the current source, so the result depends on where the pointer *is* and not on the
       * path it took or how many events the machine delivered.
       */
      readonly before: EntitySource;
    }
  /**
   * Moving a whole selection.
   *
   * Carries the sources as they were when the gesture began, and each move re-derives from
   * those rather than nudging the current ones. Accumulating deltas would drift, and — worse —
   * would make the result depend on how many pointer events the machine happened to deliver:
   * the same gesture would land somewhere slightly different on a slow machine.
   */
  | {
      readonly name: 'moving';
      readonly origin: Vec2;
      readonly before: ReadonlyMap<EntityId, EntitySource>;
    }
  | { readonly name: 'marquee'; readonly origin: Vec2; readonly current: Vec2 };

export class SelectTool implements Tool {
  readonly id = 'select';
  readonly label = 'Chọn';
  readonly shortcut = 'v';
  readonly group = 'Vẽ';

  /** Selecting, dragging and nudging all rewrite coordinates; none of them add or remove. */
  readonly restructures = false;

  private state: State = { name: 'idle' };

  onPointerDown(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (input.button !== 0) return null;

    const hit = hitTest(context.scene, input.world, context.tolerance);
    if (hit === null) {
      this.state = { name: 'marquee', origin: input.world, current: input.world };
      // Additive marquee keeps what was already selected.
      return input.modifiers.shift ? {} : { selection: [] };
    }

    const entity = context.document.entities.get(hit.id);
    const value = valueOf(context.scene, hit.id);
    const grabOffset = value?.kind === 'point' ? vec.subtract(value.point, input.world) : vec.ZERO;

    this.state = {
      name: 'pressing',
      target: hit.id,
      origin: input.world,
      additive: input.modifiers.shift,
    };

    const alreadySelected = context.selection.has(hit.id);
    const selection = input.modifiers.shift
      ? toggle(context.selection, hit.id)
      : alreadySelected
        ? [...context.selection]
        : [hit.id];

    /*
     * Which gesture a drag from here will be.
     *
     * A **single** draggable point drags itself, with snapping and constraint anchoring —
     * that is the precise editing gesture and it must not be taken away. Anything else that
     * is part of the selection moves the *whole* selection, which is what makes a triangle
     * draggable at all: its sides have no coordinates, so before this the only way to move a
     * figure was to drag its corners one at a time.
     */
    /*
     * ...and an angle mark's *number* is a third case, between the two.
     *
     * The mark as a whole is not draggable and must not become so: it has no position of its
     * own, and dragging its arc rightly moves the angle. But the hit test offers the number as
     * a separate target, and when the number is what was hit, the number is what the user meant
     * to pick up. `hit.closest` is that decision, already made — comparing against it rather
     * than re-measuring means the gesture can never disagree with what was picked.
     */
    /*
     * A graph's *formula* is the same case, and reached the same way.
     *
     * The curve itself must not be draggable: it is a formula, and dragging it would either mean
     * nothing or mean rewriting the user's algebra behind their back. The label is the one part
     * of a graph with a position of its own, and the one part that lands on top of something —
     * the y axis, or the other curve on the same figure.
     */
    const labelAt = value?.kind === 'function-graph' ? value.labelAt : null;
    const numberAt =
      value?.kind === 'angle-mark' && value.showValue
        ? angleMarkValuePosition(value)
        : (labelAt ?? null);
    const grabbingNumber = numberAt !== null && vec.distance(numberAt, hit.closest) < 1e-9;

    // A stated ratio slides along the two points it divides. See `dragResponse` for why this
    // one derived point may be dragged and a midpoint may not.
    const grabbingRatio = entity?.source.op === 'ratio-point';

    const movingSelection =
      selection.length > 1 ||
      (!grabbingNumber && !grabbingRatio && !isDraggableEntity(entity) && !isDerivedPoint(entity));

    this.pendingMove = movingSelection ? this.snapshot(context, selection) : null;
    this.pendingGrabOffset =
      !movingSelection && (grabbingNumber || grabbingRatio || isDraggableEntity(entity))
        ? grabOffset
        : null;

    return { selection, cursor: 'grabbing' };
  }

  private pendingGrabOffset: Vec2 | null = null;
  private pendingMove: ReadonlyMap<EntityId, EntitySource> | null = null;

  /**
   * The sources a move would rewrite, as they are now.
   *
   * Returns `null` when the selection has no movable ancestors — a selection of nothing but
   * intersection points, say, whose positions are consequences rather than choices. Offering
   * a drag that cannot move anything is the failure this avoids.
   */
  private snapshot(
    context: ToolContext,
    selection: readonly EntityId[]
  ): ReadonlyMap<EntityId, EntitySource> | null {
    const roots = movableRootsOf(context.document, selection);
    if (roots.length === 0) return null;

    const before = new Map<EntityId, EntitySource>();
    for (const id of roots) {
      const found = context.document.entities.get(id);
      if (found !== undefined) before.set(id, found.source);
    }
    return before.size === 0 ? null : before;
  }

  onPointerMove(context: ToolContext, input: PointerInput): ToolResponse | null {
    switch (this.state.name) {
      case 'idle': {
        /*
         * Hover feedback only — but the cursor says which of the two things a press will do.
         *
         * `pointer` for everything was the first version and it under-sold the tool: a hand
         * means "this is clickable", which a user reads as *select*, so nothing suggested that
         * a label or a picture could simply be picked up and carried. `move` says so without
         * a word, and it is the cursor every other editor uses for the same gesture.
         *
         * A derived point keeps the plain hand, because it genuinely cannot be moved — its
         * position is a consequence, and promising otherwise would be the cursor lying.
         */
        const hit = hitTest(context.scene, input.world, context.tolerance);
        if (hit === null) return { cursor: 'default' };

        const entity = context.document.entities.get(hit.id);
        return { cursor: isDerivedPoint(entity) ? 'pointer' : 'move' };
      }

      case 'pressing': {
        if (!exceedsDragThreshold(this.state.origin, input.world, context.zoom)) return null;

        if (this.pendingMove !== null) {
          this.state = {
            name: 'moving',
            origin: this.state.origin,
            before: this.pendingMove,
          };
          return this.moveResponse(context, input);
        }

        if (this.pendingGrabOffset === null) {
          // Not draggable: fall back to a marquee from where the press began.
          this.state = { name: 'marquee', origin: this.state.origin, current: input.world };
          return this.marqueeResponse(context);
        }
        const source = context.document.entities.get(this.state.target)?.source;
        if (source === undefined) return null;

        this.state = {
          name: 'dragging',
          target: this.state.target,
          origin: this.state.origin,
          grabOffset: this.pendingGrabOffset,
          before: source,
        };
        return this.dragResponse(context, input);
      }

      case 'dragging':
        return this.dragResponse(context, input);

      case 'moving':
        return this.moveResponse(context, input);

      case 'marquee':
        this.state = { name: 'marquee', origin: this.state.origin, current: input.world };
        return this.marqueeResponse(context);
    }
  }

  onPointerUp(context: ToolContext, input: PointerInput): ToolResponse | null {
    switch (this.state.name) {
      case 'idle':
        return null;

      case 'pressing':
        this.state = { name: 'idle' };
        return { cursor: 'default' };

      case 'dragging': {
        const response = this.dragResponse(context, input);
        this.state = { name: 'idle' };
        // Sealing here is what separates two successive drags of the same point into
        // two undo steps: they share a merge key, so only the gesture boundary can.
        return {
          ...response,
          preview: [],
          anchor: undefined,
          cursor: 'default',
          sealHistory: true,
        };
      }

      case 'moving': {
        const response = this.moveResponse(context, input);
        this.state = { name: 'idle' };
        return { ...response, preview: [], cursor: 'default', sealHistory: true };
      }

      case 'marquee': {
        const region = boundsOf(this.state.origin, this.state.current);
        const enclosed = entitiesWithin(context.scene, region);
        const additive = input.modifiers.shift;
        this.state = { name: 'idle' };
        return {
          selection: additive ? [...new Set([...context.selection, ...enclosed])] : enclosed,
          preview: [],
          cursor: 'default',
        };
      }
    }
  }

  onKeyDown(context: ToolContext, key: string): ToolResponse | null {
    const nudge = NUDGES[key];
    if (nudge !== undefined) return this.nudgeResponse(context, nudge);

    if (key !== 'Delete' && key !== 'Backspace') return null;
    if (context.selection.size === 0) return null;

    const actions: EditAction[] = [...context.selection].map((id) => ({
      kind: 'remove-entity' as const,
      id,
    }));
    return { actions, selection: [], label: 'Delete', sealHistory: true };
  }

  /**
   * Arrow keys move the selection by a fixed step in world units.
   *
   * A **fixed** step, not one scaled by the zoom. A nudge is for saying "a little to the left"
   * about the drawing, and a step that changed size with the camera would place things
   * differently depending on how far the user happened to be zoomed in — which turns a precise
   * gesture into an unpredictable one.
   *
   * Nudges share one merge key, so holding an arrow key produces a single undo step rather
   * than forty. They are sealed by any other edit, which is what makes "nudge, nudge, nudge,
   * undo" put things back where they started.
   */
  private nudgeResponse(context: ToolContext, by: Vec2): ToolResponse | null {
    if (context.selection.size === 0) return null;

    const actions = transformActions(context.document, context.selection, translation(by));
    if (actions.length === 0) return null;

    return { actions, label: 'Nudge', mergeKey: 'nudge' };
  }

  cancel(): ToolResponse | null {
    if (this.state.name === 'idle') return null;
    this.state = { name: 'idle' };
    this.pendingGrabOffset = null;
    this.pendingMove = null;
    return { preview: [], anchor: undefined, cursor: 'default' };
  }

  reset(): void {
    this.state = { name: 'idle' };
    this.pendingGrabOffset = null;
    this.pendingMove = null;
  }

  /**
   * The extent of the selection, drawn as an outline with no grips.
   *
   * No grips because this tool does not resize: showing one and refusing to act on it is worse
   * than showing none. What the outline is for is the question a selection halo answers badly
   * on a figure of twenty entities — *how much have I got hold of?* — and it is the same
   * outline the transform tool grips, so pressing that tool's key changes what the frame can
   * do without changing where it is.
   *
   * Suppressed mid-gesture. During a drag the frame would be measuring a figure that is moving
   * under it, so it would lag by exactly one event and read as a fault.
   */
  overlayBox(context: ToolContext): TransformBox | null {
    if (this.state.name !== 'idle') return null;

    const box = selectionBounds(context.scene.values, context.selection);
    if (box === null) return null;

    const frame = frameOf(box);
    if (!isUsable(frame)) return null;

    return boxOf(frame, geometryFor(context.zoom), []);
  }

  /* ------------------------------------------------------------------ dragging */

  /**
   * Moves the grabbed entity.
   *
   * A **free point** takes a new position. A **locus point** takes a new parameter —
   * projecting the pointer onto its host rather than assigning a coordinate, which is
   * what keeps it exactly on the host instead of near it.
   *
   * The anchor is passed through so the constraint solver pulls towards the pointer while
   * still satisfying the equations, rather than the pointer winning outright.
   */
  private dragResponse(context: ToolContext, input: PointerInput): ToolResponse {
    if (this.state.name !== 'dragging') return {};
    const { target, grabOffset, origin, before } = this.state;

    const entity = context.document.entities.get(target);
    if (entity === undefined) return {};

    const desired = vec.add(input.world, grabOffset);

    /*
     * A point on a free plane slides **in the plane**, not across the page.
     *
     * The pointer gives a page position and the source holds `(u, v)` in the plane's own axes, so
     * the drag has to run the projection backwards. That is exact rather than iterative: a frame
     * is affine, so the page images of `u` and `v` are two fixed vectors and the answer is one
     * 2×2 solve. The alternative — nudging `(u, v)` until the point lands near the pointer —
     * would drift and would feel heavy at the far end of a tilted plane, where a page pixel is
     * worth several world units.
     *
     * Clamped to the drawn parallelogram. Without it the point leaves the wash it is supposed to
     * lie on, and the figure reads "M does not belong to (P)" while M does. The plane is not
     * grown to fit: `size` is the author's choice about how much of an infinite surface to show,
     * and a plane that swells whenever a point is dragged is something nobody asked for.
     */
    if (entity.source.op === 'plane-point') {
      const plane = valueOf(context.scene, entity.source.plane);
      const placed = inPlaneAt(plane, desired);
      if (placed === null) return {};

      return {
        actions: [
          {
            kind: 'update-entity',
            entity: { ...entity, source: { ...entity.source, u: placed.u, v: placed.v } },
          },
        ],
        // One undo step for the whole gesture, like every other drag here.
        mergeKey: `drag:${entity.id}`,
        cursor: 'grabbing',
      };
    }

    if (entity.source.op === 'locus-point') {
      /*
       * Along the host, and — when the host is a polygon — along the *same side* it was put on.
       *
       * Not re-snapped to whichever side is nearest, deliberately. A point placed on AB is a
       * point on AB: dragging it past B should run it to the corner and stop, not hand it to
       * BC. The alternative reads as the point escaping, and it silently rewrites what the
       * figure claims — "M thuộc AB" becoming "M thuộc BC" because the pointer wandered.
       */
      const { host: hostId, edge } = entity.source;
      const host = hostCurve(valueOf(context.scene, hostId), edge);
      if (host === null) return {};
      const parameter = clampParameter(host, parameterAt(host, desired));
      return {
        actions: [
          {
            kind: 'update-entity',
            entity: {
              ...entity,
              source: {
                op: 'locus-point',
                host: hostId,
                t: parameter,
                ...(edge === undefined ? {} : { edge }),
              },
            },
          },
        ],
        cursor: 'grabbing',
      };
    }

    /*
     * A stated ratio, slid along the two points it divides.
     *
     * The panel is the exact way to set this and stays the point of it — but the panel's own
     * note says the intended flow is "I put it roughly right, now say exactly what it is", and
     * that first half needs a gesture. It matters more since a click on a solid's edge builds
     * one of these: having placed M on SA, the natural next move is to nudge it, and finding
     * that the point cannot be nudged reads as the point being stuck.
     *
     * A **midpoint** stays undraggable, and the difference is not an oversight. "M is the
     * midpoint of AB" is a statement the drawing makes; a midpoint you can drag teaches that M
     * is a thing you place. A ratio is a placement — that is what the fraction is for.
     *
     * Not clamped to the segment. Past the far end is `t > 1`, which is a real sentence in the
     * book — "trên tia đối của tia AB lấy M sao cho…" — and the panel already says so.
     */
    if (entity.source.op === 'ratio-point') {
      const a = asPoint(valueOf(context.scene, entity.source.a));
      const b = asPoint(valueOf(context.scene, entity.source.b));
      if (a === null || b === null) return {};

      const along = vec.subtract(b, a);
      const lengthSquared = vec.dot(along, along);
      if (lengthSquared < 1e-12) return {};

      const t = vec.dot(vec.subtract(desired, a), along) / lengthSquared;
      return {
        actions: [
          {
            kind: 'update-entity',
            entity: { ...entity, source: { ...entity.source, t } },
          },
        ],
        cursor: 'grabbing',
        label: 'Move point',
        mergeKey: `drag:${entity.id}`,
      };
    }

    if (entity.source.op !== 'free-point') {
      return this.translateResponse(entity, before, vec.subtract(input.world, origin));
    }

    const snapped = snap(
      {
        scene: context.scene,
        zoom: context.zoom,
        exclude: new Set([target]),
        settings: context.snapSettings,
      },
      desired
    );
    const position = input.modifiers.primary ? desired : snapped.world;

    return {
      actions: [
        { kind: 'update-entity', entity: { ...entity, source: { op: 'free-point', position } } },
      ],
      anchor: { point: target, target: position },
      snap: snapped,
      cursor: 'grabbing',
      label: 'Move point',
      mergeKey: `drag:${target}`,
    };
  }

  /**
   * Moves the whole selection by however far the pointer has travelled.
   *
   * Everything is re-derived from the snapshot taken at press time, so the result depends only
   * on where the pointer is — not on the path it took or how many events arrived. Dropping the
   * gesture and repeating it lands in exactly the same place.
   *
   * There is no snapping here, deliberately. Snapping asks "which existing point is this one
   * near", which is a sensible question for one point and a meaningless one for a figure made
   * of twenty. Precise placement is still available by dragging a single vertex.
   */
  private moveResponse(context: ToolContext, input: PointerInput): ToolResponse {
    if (this.state.name !== 'moving') return {};

    const by = vec.subtract(input.world, this.state.origin);
    const restored = { ...context.document, entities: new Map(context.document.entities) };

    for (const [id, source] of this.state.before) {
      const current = restored.entities.get(id);
      if (current !== undefined) restored.entities.set(id, { ...current, source });
    }

    const actions = transformActions(restored, this.state.before.keys(), translation(by));
    if (actions.length === 0) return { cursor: 'grabbing' };

    return {
      actions,
      cursor: 'grabbing',
      label: 'Move selection',
      // One undo step per gesture, not one per pointer event.
      mergeKey: `move:${[...this.state.before.keys()].join(',')}`,
    };
  }

  /**
   * Moves anything that carries its own coordinates but is not a point.
   *
   * ## Why this exists
   *
   * `isDraggable` has always said yes to text, measurements, images, freehand strokes and
   * free ellipses — and the drag handler only ever implemented points. So the cursor turned
   * to *grabbing*, the gesture ran, and nothing moved: a label typed in the wrong place could
   * not be picked up and carried anywhere. That is the same defect as a cursor that invites a
   * click which does nothing, and it is worse here, because the user has no reason at all to
   * suspect the object is special.
   *
   * ## Why a label moves differently from everything else
   *
   * A label's `offset` is measured from its anchor, so dragging one has to *add* to that
   * offset — which is the opposite of what a group transform does. When a whole figure moves,
   * the anchor is moving too, and translating the offset as well would move the label twice.
   * Here the anchor is standing still and only the label is being carried, so the offset is
   * exactly the thing that changes.
   *
   * The consequence is the behaviour a user wants without asking for it: dragging `A` away
   * from its vertex nudges the label and leaves the point where it is.
   *
   * ## An angle mark moves *part* of itself
   *
   * The mark has no coordinates — it is three points and a rule, and dragging it anywhere is
   * meaningless. Its **number** is the exception: `56.3°` is placed by a rule that knows the
   * angle and nothing else about the drawing, so it lands on an edge, or on a second mark's
   * number, often enough that no better rule would fix it. What is being moved is the number,
   * and `valueOffset` is where it goes, added exactly as a label's offset is and for the same
   * reason — the mark itself is standing still.
   */
  private translateResponse(entity: Entity, before: EntitySource, by: Vec2): ToolResponse {
    const moved =
      before.op === 'text' || before.op === 'measurement'
        ? { ...before, offset: vec.add(before.offset, by) }
        : before.op === 'angle-mark'
          ? { ...before, valueOffset: vec.add(before.valueOffset ?? vec.ZERO, by) }
          : // A graph's formula, for the same reason and in the same shape: the curve stands
            // still and the writing beside it is what moves.
            before.op === 'function-graph'
            ? { ...before, labelOffset: vec.add(before.labelOffset ?? vec.ZERO, by) }
            : transformSource(before, translation(by));

    if (moved === null) return {};

    return {
      actions: [{ kind: 'update-entity', entity: { ...entity, source: moved } }],
      cursor: 'grabbing',
      label:
        before.op === 'text'
          ? 'Move label'
          : before.op === 'angle-mark'
            ? 'Move angle value'
            : before.op === 'function-graph'
              ? 'Move formula'
              : 'Move object',
      // One undo step per gesture, not one per pointer event.
      mergeKey: `drag:${entity.id}`,
    };
  }

  private marqueeResponse(context: ToolContext): ToolResponse {
    if (this.state.name !== 'marquee') return {};
    const { origin, current } = this.state;
    const preview: Evaluated[] = [
      {
        kind: 'polyline',
        points: [origin, { x: current.x, y: origin.y }, current, { x: origin.x, y: current.y }],
        closed: true,
      },
    ];
    void context;
    return { preview, cursor: 'crosshair' };
  }
}

/** World units per arrow-key press. */
const NUDGE_STEP = 1;

const NUDGES: Readonly<Record<string, Vec2 | undefined>> = {
  ArrowLeft: vec.vec2(-NUDGE_STEP, 0),
  ArrowRight: vec.vec2(NUDGE_STEP, 0),
  // Up is up: the world's y axis points up, unlike the screen's.
  ArrowUp: vec.vec2(0, NUDGE_STEP),
  ArrowDown: vec.vec2(0, -NUDGE_STEP),
};

/** Whether pressing this entity begins a single-point drag rather than a selection move. */
function isDraggableEntity(entity: { readonly source: EntitySource } | undefined): boolean {
  return entity !== undefined && isDraggable(entity.source);
}

/**
 * A point whose position is a *consequence* — a midpoint, an intersection, a circumcentre.
 *
 * These are the one thing a drag must refuse to move, and the reason is pedagogical rather
 * than technical. A midpoint has no position of its own to write, but neither does a segment,
 * and a segment is grabbed to move a figure all the time. The difference is what the object
 * *says*: "M is the midpoint of AB" is the lesson, and letting the user drag M — even if the
 * editor obliged by moving A and B — would teach that M is a thing you place.
 *
 * A derived *shape* carries no such claim. Nobody reads a circumcircle as a position they are
 * forbidden to touch; they read it as the figure, and grabbing the figure to move it is
 * exactly right.
 *
 * ## And a solid's corner is a shape, not a claim
 *
 * `A` on a cube says which corner is which. It is a *name* for a part of a body, not a statement
 * about where other points are, so there is no lesson for a drag to contradict — grabbing a solid
 * by a corner to move it is what a hand does with a block.
 *
 * It also has to be, now that corners are entities. An isometric cube puts two of its eight
 * corners exactly on the centre of the hexagon, which is where the anchor sits: refusing the drag
 * there left the commonest solid in the product unmovable from the middle, with nothing to say
 * why. Letting it through moves the anchor by the ordinary upward closure, and the whole figure
 * follows — the same path that already moves a triangle grabbed by one of its sides.
 */
function isDerivedPoint(entity: { readonly source: EntitySource } | undefined): boolean {
  if (entity === undefined) return false;
  if (entity.source.op === 'solid-vertex') return false;
  return kindOf(entity.source) === 'point' && !isDraggable(entity.source);
}

function toggle(selection: ReadonlySet<EntityId>, id: EntityId): EntityId[] {
  const next = new Set(selection);
  if (!next.delete(id)) next.add(id);
  return [...next];
}

function boundsOf(a: Vec2, b: Vec2) {
  return {
    minX: Math.min(a.x, b.x),
    minY: Math.min(a.y, b.y),
    maxX: Math.max(a.x, b.x),
    maxY: Math.max(a.y, b.y),
  };
}
